/* ==========================================================================
   PiHOT Motion Layer (v0.1)
   Declarative GSAP animations driven by data attributes:
     data-p-reveal="up|fade|scale"   reveal on enter (batched + staggered)
     data-p-delay="0.2"              extra delay in seconds
     data-p-parallax="0.15"          scrubbed vertical parallax (fraction of height)
     data-p-split="lines|words"      SplitText headline reveal
   Requires assets/animations.min.js (GSAP + ScrollTrigger + SplitText).
   ========================================================================== */

(() => {
  const root = document.documentElement;

  if (!window.gsap || !window.ScrollTrigger) {
    root.classList.remove('p-motion-ready');
    return;
  }

  const { gsap, ScrollTrigger } = window;
  const hasSplitText = typeof window.SplitText !== 'undefined';
  if (hasSplitText) gsap.registerPlugin(ScrollTrigger, window.SplitText);

  const EASE = {
    out: 'power3.out',
    hero: 'expo.out',
    inOut: 'power2.inOut',
    scrub: 'none',
  };

  const DURATION = {
    fast: 0.2,
    base: 0.4,
    slow: 0.8,
    slower: 1.2,
  };

  const REVEAL_FROM = {
    up: { autoAlpha: 0, y: 40 },
    fade: { autoAlpha: 0 },
    scale: { autoAlpha: 0, scale: 0.96 },
  };

  const contexts = new WeakMap();

  const revealIn = (elements) => {
    gsap.to(elements, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      duration: DURATION.slow,
      ease: EASE.out,
      stagger: 0.08,
      delay: (i, el) => parseFloat(el.dataset.pDelay) || 0,
      overwrite: true,
      clearProps: 'transform',
    });
  };

  const setupReveals = (scope) => {
    const targets = gsap.utils.toArray('[data-p-reveal]', scope);
    if (!targets.length) return;

    targets.forEach((el) => gsap.set(el, REVEAL_FROM[el.dataset.pReveal] || REVEAL_FROM.up));

    ScrollTrigger.batch(targets, {
      start: 'top 88%',
      once: true,
      onEnter: revealIn,
    });
  };

  const setupParallax = (scope) => {
    gsap.utils.toArray('[data-p-parallax]', scope).forEach((el) => {
      const amount = parseFloat(el.dataset.pParallax) || 0.15;
      gsap.fromTo(
        el,
        { yPercent: -amount * 50 },
        {
          yPercent: amount * 50,
          ease: EASE.scrub,
          scrollTrigger: {
            trigger: el.parentElement || el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6,
          },
        }
      );
    });
  };

  const setupSplitText = (scope) => {
    if (!hasSplitText) return;

    gsap.utils.toArray('[data-p-split]', scope).forEach((el) => {
      const type = el.dataset.pSplit === 'words' ? 'words' : 'lines';

      window.SplitText.create(el, {
        type,
        mask: type,
        autoSplit: true,
        onSplit(self) {
          return gsap.from(self[type], {
            yPercent: 110,
            duration: DURATION.slower,
            ease: EASE.hero,
            stagger: 0.08,
            scrollTrigger: {
              trigger: el,
              start: 'top 85%',
              once: true,
            },
          });
        },
      });
    });
  };

  const init = (scope = document) => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        setupReveals(scope);
        setupParallax(scope);
        setupSplitText(scope);
      });

      mm.add('(prefers-reduced-motion: reduce)', () => {
        gsap.set(gsap.utils.toArray('[data-p-reveal]', scope), { autoAlpha: 1, clearProps: 'transform' });
      });
    }, scope);

    if (scope !== document) contexts.set(scope, ctx);
    return ctx;
  };

  const destroy = (scope) => {
    const ctx = contexts.get(scope);
    if (!ctx) return;
    ctx.revert();
    contexts.delete(scope);
  };

  const enableRefraction = () => {
    const brands = navigator.userAgentData?.brands || [];
    const isChromium = brands.some((b) => /Chromium/i.test(b.brand));
    if (!isChromium) return;
    document.querySelectorAll('.p-liquid-glass[data-p-refract]').forEach((el) => el.classList.add('is-refractive'));
  };

  init(document);
  enableRefraction();
  window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    enableRefraction();
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window.PiHOT = window.PiHOT || {};
  window.PiHOT.motion = { init, destroy, EASE, DURATION };
})();
